/**
 * Client-side, per-session SpotQL routing discriminator — a plain Map keyed by
 * bach session id, acting as the source of truth for `answerKind`.
 *
 * `answerKind` is known on the client the moment an answer loads, but the Apollo
 * cache is NOT a reliable place to read it back, for two reasons:
 *
 *   1. Late — it only lands on the `BachSessionId` entity when the
 *      LoadExistingAnswerSession *response* echoes it (via the `bachSession`
 *      fragment). Ops that fire before that (e.g. the first GetChartWithData /
 *      GetAnswerDataByQuery) would read a null answerKind.
 *   2. Clobbered — that same `bachSession` fragment is spread into dozens of
 *      other bach operations whose responses return `answerKind: null` from
 *      Bach (only loadSession echoes a real value). Each such response
 *      overwrites the cached answerKind back to null.
 *
 * This Map holds answerKind synchronously and is immune to both, so every
 * consumer resolves the same value regardless of cache timing/clobbering:
 *   - session builders — `loadAnswerSession` (so a 2nd load from another
 *     container that doesn't thread answerKind still sends the discriminator)
 *     and `getSession` (fills it on a cache miss, so downstream bach ops route
 *     to `/bach/spotql_answer`).
 *   - UI capability gates with no client/session handy (e.g. the sage tokens
 *     bar, hidden for spotql).
 *
 * Keyed by bach session id (not gen no), so it survives gen-no advances within
 * the same session. Never cleared — one tiny entry per answer session, lives
 * for the page lifetime.
 */

/**
 * `Spotql` routes session ops to `/bach/spotql_answer`; absent or `Sage` keeps
 * the classic Token/NLS path.
 */
export enum AnswerKindEnum {
    Spotql = 'spotql',
    Sage = 'sage',
}

export type AnswerKind = 'sage' | 'spotql';

const answerKindBySessionId = new Map<string, string>();

/** Records the answerKind for a session. No-op for empty inputs. */
export const rememberAnswerKind = (
    sessionId: string,
    answerKind?: string | null,
): void => {
    if (sessionId && answerKind) {
        answerKindBySessionId.set(sessionId, answerKind);
    }
};

/** Returns the remembered answerKind for a session, if any. */
export const recallAnswerKind = (sessionId?: string): string | undefined =>
    sessionId ? answerKindBySessionId.get(sessionId) : undefined;
